import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

// Create the context
const UserDataContext = createContext(null);

/**
 * Provider component for persisting extended user data
 * @param {object} props - Component props
 * @returns {JSX.Element} User data provider
 */
export const UserDataProvider = ({ children }) => {
  const { user: authUser, account, unit } = useAuth();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize from localStorage and sync with auth user
  useEffect(() => {
    const initializeUserData = () => {
      try {
        // console.log('UserDataContext: Initializing with authUser:', authUser);
        setLoading(true);
        
        // Try to load from localStorage first
        const savedUserData = localStorage.getItem('userData');
        const parsedData = savedUserData ? JSON.parse(savedUserData) : null;
        
        // If we have auth user data and it has a UUID, we update our stored data
        if (authUser && authUser.uuid) {
          // console.log('UserDataContext: Auth user found with UUID:', authUser.uuid);
          
          // Create a merged object combining all available data
          const combinedData = {
            // Start with any existing localStorage data for this user
            ...(parsedData && parsedData.uuid === authUser.uuid ? parsedData : {}),
            // Add auth user data (which takes precedence for overlapping fields)
            ...authUser,
            // Add account data if available
            ...(account ? { account } : {}),
            // Add unit data if available
            ...(unit ? { unit } : {})
          };
          
          // console.log('UserDataContext: Setting combined user data');
          setUserData(combinedData);
          
          // Save to localStorage
          localStorage.setItem('userData', JSON.stringify(combinedData));
        } else if (parsedData) {
          // No auth user but we have localStorage data
          // console.log('UserDataContext: No auth user, using localStorage data');
          setUserData(parsedData);
        } else {
          // console.log('UserDataContext: No user data available');
          setUserData(null);
        }
      } catch (error) {
        // console.error('Error initializing user data:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeUserData();
  }, [authUser, account, unit]);

  /**
   * Update user data with new information
   * This merges the new data with existing data and saves to localStorage
   * @param {object} newData - New user data to merge with existing data
   */
  const updateUserData = useCallback((newData) => {
    // console.log('UserDataContext: Updating user data with:', newData);
    
    setUserData(prevData => {
      if (!prevData) return newData;
      
      // Make sure we preserve critical fields like siteName and unitId
      const updatedData = { 
        ...prevData, 
        ...newData,
        // Ensure these fields are explicitly set if available from any source
        siteName: newData.siteName || prevData.siteName || (authUser ? authUser.siteName : null),
        unitId: newData.unitId || prevData.unitId || (authUser ? authUser.unitId : null)
      };
      
      // Log the merged data
      // console.log('UserDataContext: Merged data includes siteName:', updatedData.siteName, 'unitId:', updatedData.unitId);
      
      // Only save to localStorage if we have a UUID
      if (updatedData.uuid) {
        localStorage.setItem('userData', JSON.stringify(updatedData));
      }
      
      return updatedData;
    });
  }, [authUser]);

  /**
   * Replace all user data (instead of merging)
   * @param {object} data - Complete user data to set
   */
  const setCompleteUserData = useCallback((data) => {
    // console.log('UserDataContext: Setting complete user data');
    
    // Ensure critical fields are set
    const completeData = {
      ...data,
      siteName: data.siteName || (authUser ? authUser.siteName : null),
      unitId: data.unitId || (authUser ? authUser.unitId : null)
    };
    
    // Log the complete data
    // console.log('UserDataContext: Complete data includes siteName:', completeData.siteName, 'unitId:', completeData.unitId);
    
    setUserData(completeData);
    
    // Only save to localStorage if we have a UUID
    if (completeData && completeData.uuid) {
      localStorage.setItem('userData', JSON.stringify(completeData));
    }
  }, [authUser]);

  /**
   * Clear stored user data
   * Used primarily during logout
   */
  const clearUserData = useCallback(() => {
    // console.log('UserDataContext: Clearing user data');
    setUserData(null);
    localStorage.removeItem('userData');
  }, []);

  /**
   * Refresh user data from auth context
   * Useful when auth user data has been updated
   */
  const refreshFromAuth = useCallback(() => {
    // console.log('UserDataContext: Refreshing from auth context');
    
    if (authUser && authUser.uuid) {
      const refreshedData = {
        ...(userData || {}),
        ...authUser,
        ...(account ? { account } : {}),
        ...(unit ? { unit } : {})
      };
      
      setUserData(refreshedData);
      localStorage.setItem('userData', JSON.stringify(refreshedData));
    }
  }, [authUser, account, unit, userData]);

  return (
    <UserDataContext.Provider
      value={{
        userData,
        loading,
        updateUserData,
        setCompleteUserData,
        clearUserData,
        refreshFromAuth
      }}
    >
      {children}
    </UserDataContext.Provider>
  );
};

/**
 * Hook to use the user data context
 * @returns {object} User data context
 */
export const useUserData = () => {
  const context = useContext(UserDataContext);
  if (!context) {
    throw new Error('useUserData must be used within a UserDataProvider');
  }
  return context;
}; 